import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, share } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { SurveyAuthService } from '../../../../shared/survey-auth.service';
import { Survey } from '../models/survey';

@Injectable()
export class SurveyService {
  private surveyListCached: Survey[] = null;
  private surveyListObservable: Observable<Survey[]>;

  private surveyCached: Survey;

  constructor(private http: HttpClient, private auth: SurveyAuthService) {}

  getSurveyDownloadUrl(survey_id: number) {
    const url = `${environment.surveyApiHost}/api/v1/surveys/${survey_id}/download_xml/ca18c2hd77SsnHHEK/`;
    return url;
  }

  getSurveyVariablesDownloadUrl(survey_id: number, encoding: string = null) {
    let url = `${environment.surveyApiHost}/api/v1/surveys/${survey_id}/download_column_summary_csv/ca18c2hd77SsnHHEK/`;
    if (encoding) {
      url = url + `?encoding=${encoding}`;
    }
    return url;
  }

  getSurveyResultsCSVDownloadUrl(survey_id: number, encoding: string = null) {
    let url = `${environment.surveyApiHost}/api/v1/surveys/${survey_id}/download_results_csv/ca18c2hd77SsnHHEK/`;
    if (encoding) {
      url = url + `?encoding=${encoding}`;
    }
    return url;
  }

  getSurveyResultsExcelDownloadUrl(survey_id: number) {
    const url = `${environment.surveyApiHost}/survey_admin/download_results_excel/${survey_id}`;
    return url;
  }

  // fixme move to email list service
  getTokenString(): string {
    return `Token ${this.auth.token()}`;
  }

  // fetch survey list from server or return the cached list
  getSurveys(what: string): Observable<Survey[]> {
    if (this.surveyListCached) {
      // if `surveyListCached` is available just return it as `Observable`
      return of(this.surveyListCached);
    } else if (this.surveyListObservable) {
      // if `this.surveyListObservable` is set then the request is in progress
      // return the `Observable` for the ongoing request
      return this.surveyListObservable;
    } else {
      // create the request, store the `Observable` for subsequent subscribers
      const get_url = `${environment.surveyApiHost}/api/v1/surveys/?what=${what}`;
      console.log('GET ' + get_url);
      let headers = new HttpHeaders();
      headers = headers.append('Content-Type', 'application/json');
      const tokenStr = `Token ${this.auth.token()}`;
      headers = headers.append('Authorization', tokenStr);

      this.surveyListObservable = this.http
        .get(get_url, { headers: headers })
        .pipe(
          map((response: any) => {
            // when the cached data is available we don't need the `Observable` reference anymore
            this.surveyListObservable = null;
            const json = response;
            this.surveyListCached = json;
            return json;
          }), // make it shared so more than one subscriber can get the result
          catchError(this.handleError),
          share()
        );
      return this.surveyListObservable;
    }
  }

  // clear survey list cache
  clearSurveyListCache() {
    this.surveyListCached = null;
  }

  getSurvey(id: number, lang: string): Observable<Survey> {
    console.log('SurveyService.getSurvey invoked for id', id);

    let headers = new HttpHeaders();
    headers = headers.append('Content-Type', 'application/json');
    const tokenStr = `Token ${this.auth.token()}`;
    headers = headers.append('Authorization', tokenStr);
    let surveyUrl = `${environment.surveyApiHost}/api/v1/surveys/${id}/?inline=1`;
    if (lang != null) surveyUrl = surveyUrl + `&lang=${lang}`;

    return this.http
      .get(surveyUrl, { headers: headers })
      .map(this.processGetSurveyResponse)
      .catch(this.handleError);
  }

  processGetSurveyResponse(response: Response) {
    console.log('response.json:', response);
    this.surveyCached = Survey.fromJSON(response);
    return this.surveyCached;
  }

  // Create Survey
  addSurvey(survey: Survey, from_survey_id: number): Observable<Survey> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    const tokenStr = `Token ${this.auth.token()}`;
    headers = headers.append('Authorization', tokenStr);

    let surveysUrl = `${environment.surveyApiHost}/api/v1/surveys/`;
    if (from_survey_id != null) {
      surveysUrl = surveysUrl + `?from_survey=${from_survey_id}`;
    }
    console.log('POST ', surveysUrl, JSON.stringify(survey));
    return this.http
      .post(surveysUrl, JSON.stringify(survey), { headers: headers })
      .map(this.processPostPutResponse)
      .catch(this.handleError);
  }

  // update survey
  updateSurvey(survey: Survey): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.append('Content-Type', 'application/json');
    const tokenStr = `Token ${this.auth.token()}`;
    headers = headers.append('Authorization', tokenStr);

    const surveyUrl = `${environment.surveyApiHost}/api/v1/surveys/${survey.id}/`;
    console.log('PUT ', surveyUrl);

    return this.http
      .put(surveyUrl, JSON.stringify(survey), { headers: headers })
      .catch(this.handleError);
  }

  private processPostPutResponse(res: HttpResponse<any>): Survey {
    console.log('processPostResponse: res: ', res);
    const body = res.body.json();
    console.log('processResponse: body: ', body);
    const survey: Survey = Object.create(Survey.prototype);
    const obj = Object.assign(survey, body, {});
    return obj || {};
  }

  // Delete Survey
  deleteSurvey(survey: Survey): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.append('Content-Type', 'application/json');
    const tokenStr = `Token ${this.auth.token()}`;
    headers = headers.append('Authorization', tokenStr);

    const url = `${environment.surveyApiHost}/api/v1/surveys/${survey.id}/`;

    return this.http
      .delete(url, { headers: headers })
      .map(response => {
        // remove deleted survey from the cache
        if (this.surveyListCached) {
          const i: number = this.surveyListCached.indexOf(survey);
          if (i >= 0) {
            this.surveyListCached.splice(i, 1);
            console.log('survey deleted from cache');
          }
        }
      })
      .catch(this.handleError);
  }

  private handleError(error: HttpResponse<any> | any) {
    // In a real world app, we might use a remote logging infrastructure
    let errMsg: string;
    if (error instanceof HttpResponse) {
      console.log(error);
      const body = error.body.json() || '';
      const err = body.error || JSON.stringify(body);
      errMsg = `${error.status} - ${error.statusText || ''} ${err}`;
    } else {
      errMsg = error.message ? error.message : error.toString();
    }
    console.error(errMsg);
    return Observable.throw(errMsg);
  }
}
